import { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../../ApiUrl";
import { createEmptyEmployment, createEmptyProject } from "../constants";

const EMPLOYMENT_URL = `${API_BASE}/employment-records/`;
const PROJECT_URL = `${API_BASE}/project-records/`;
const PROJECT_BULK_URL = `${API_BASE}/project-records/bulk/`;

export const useExperienceRecords = (professionalProfileId, tenantId) => {
  const [employmentRecords, setEmploymentRecords] = useState([
    createEmptyEmployment(),
  ]);
  const [projectRecords, setProjectRecords] = useState([createEmptyProject()]);

  // ----- Hydration helpers -----
  const hydrateEmploymentFromRecords = (records) => {
    if (!Array.isArray(records) || records.length === 0) {
      return;
    }
    setEmploymentRecords(
      records.map((rec) => ({
        id: `emp-${rec.id}`,
        employer: rec.employer_name_snapshot || "",
        title: rec.job_title || "",
        employment_type: rec.employment_type || "",
        location: rec.city || "",
        country: rec.country_code || "",
        start_date: rec.start_date || "",
        end_date: rec.end_date || "",
        is_current: !!rec.is_current,
        duties: rec.duties || "",
        evidence_file: null,
        file_name: "",
        errors: {},
      })),
    );
  };

  const hydrateProjectsFromRecords = (records) => {
    if (!Array.isArray(records) || records.length === 0) return;
    setProjectRecords(
      records.map((rec) => {
        const scope_field_values = {};
        (rec.project_scopes || []).forEach((ps) => {
          (ps.scope_responses || []).forEach((resp) => {
            const key = `${ps.scope}:${resp.form_field}`;

            // Normalize: unwrap single-element arrays
            const rawValue = resp.value;
            const normalizedValue = Array.isArray(rawValue)
              ? rawValue.length === 1
                ? rawValue[0]
                : rawValue
              : rawValue;

            const existing = scope_field_values[key];

            if (existing === undefined) {
              // First occurrence
              scope_field_values[key] = normalizedValue;
            } else if (Array.isArray(existing)) {
              // Already an array → append
              scope_field_values[key] = [...existing, normalizedValue];
            } else {
              // Was scalar → promote to array
              scope_field_values[key] = [existing, normalizedValue];
            }
          });
        });

        return {
          id: `proj-${rec.id}`,
          project_name: rec.project_name || "",
          client_name:
            rec.client_name_snapshot || rec.client_organization || "",
          employer: rec.employer_organization || "",
          role_id: rec.role_title || null,
          location: rec.city || "",
          country: rec.country_code || "",
          start_date: rec.start_date || "",
          end_date: rec.end_date || "",
          is_current: !!rec.is_current,
          allocation_percent: rec.allocation_percent ?? "",
          verified_field_days: rec.verified_field_days ?? "",
          is_primary: !!rec.is_primary_assignment,
          client_visibility: rec.client_visibility || "SHOW_CLIENT_NAME",
          responsibilities: rec.responsibilities || "",
          achievements: rec.achievements || "",
          industry_id: rec.industry_classification || null,
          scope_ids: (rec.project_scopes || []).map((ps) => ps.scope),
          scope_field_values,
          evidence_file: null,
          file_name: "",
          errors: {},
        };
      }),
    );
  };

  const hydrateFromRelatedRecords = (data) => {
    if (!data) return;

    console.log("Hydrating experience from data:", data);

    if (data.employment_records && Array.isArray(data.employment_records)) {
      console.log("Found employment_records:", data.employment_records.length);
      hydrateEmploymentFromRecords(data.employment_records);
    }

    if (data.project_records && Array.isArray(data.project_records)) {
      console.log("Found project_records:", data.project_records.length);
      hydrateProjectsFromRecords(data.project_records);
    }

    if (data.experience) {
      const expData = data.experience;
      if (
        expData.employment_records &&
        Array.isArray(expData.employment_records)
      ) {
        hydrateEmploymentFromRecords(expData.employment_records);
      }
      if (expData.project_records && Array.isArray(expData.project_records)) {
        hydrateProjectsFromRecords(expData.project_records);
      }
    }

    if (data.employment && Array.isArray(data.employment)) {
      hydrateEmploymentFromRecords(data.employment);
    }
    if (data.projects && Array.isArray(data.projects)) {
      hydrateProjectsFromRecords(data.projects);
    }
  };

  useEffect(() => {
    if (!professionalProfileId) return;

    const loadExperience = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/professional-profiles/${professionalProfileId}/related-records/`,
        );
        const data = res?.data?.data || res?.data;

        hydrateFromRelatedRecords(data);
      } catch (err) {
        console.error(
          "Failed to load experience records from related-records API",
          err,
        );
      }
    };

    loadExperience();
  }, [professionalProfileId]);

  // ----- Extract backend IDs -----
  const extractEmploymentBackendId = (recordId) => {
    const match = /^emp-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };

  const extractProjectBackendId = (recordId) => {
    const match = /^proj-(\d+)$/.exec(String(recordId));
    return match ? match[1] : null;
  };

  // ----- Build payloads -----
  const buildEmploymentPayload = () => ({
    professional_id: professionalProfileId,
    employment_records: employmentRecords
      .filter((rec) => rec.employer.trim() || rec.title.trim())
      .map((rec) => ({
        _backendId: extractEmploymentBackendId(rec.id),
        employer_name_snapshot: rec.employer || "",
        job_title: rec.title || "",
        country_code: rec.country || "",
        city: rec.location || "",
        duties: rec.duties || "",
        start_date: rec.start_date || null,
        end_date: rec.is_current ? null : rec.end_date || null,
        is_current: !!rec.is_current,
        status: "DRAFT",
        tenant: tenantId || "",
        verification_status: "SELF_DECLARED",
        resume_visibility: "NEVER",
      })),
  });

  const buildProjectPayload = () => ({
    professional: professionalProfileId,
    project_records: projectRecords
      .filter((rec) => rec.project_name.trim() || rec.client_name.trim())
      .map((rec) => ({
        _backendId: extractProjectBackendId(rec.id),
        project_name: rec.project_name || "",
        employer_organization: rec.employer || "",
        client_organization: rec.client_name || "",
        client_name_snapshot: rec.client_name || "",
        client_visibility: rec.client_visibility || "SHOW_CLIENT_NAME",
        country_code: rec.country || "",
        city: rec.location || "",
        role_title: rec.role_id || null,
        start_date: rec.start_date || null,
        end_date: rec.is_current ? null : rec.end_date || null,
        is_current: !!rec.is_current,
        allocation_percent:
          rec.allocation_percent === "" || rec.allocation_percent == null
            ? null
            : Number(rec.allocation_percent),
        is_primary_assignment: !!rec.is_primary,
        working_arrangement: "",
        engagement_explanation: "",
        verified_field_days:
          rec.verified_field_days === "" || rec.verified_field_days == null
            ? 0
            : Number(rec.verified_field_days),
        responsibilities: rec.responsibilities || "",
        achievements: rec.achievements || "",
        verification_status: "SELF_DECLARED",
        status: "DRAFT",
        experience_classification: "",
        industry_classification: rec.industry_id || null,
        project_scopes: (rec.scope_ids || []).map((scopeId) => ({
          scope: scopeId,
          activity_summary: rec.scope_activity_summary?.[scopeId] || "",
          verification_status: "SELF_DECLARED",
          status: "DRAFT",
          scope_responses: Object.entries(rec.scope_field_values || {})
            .filter(([key]) => key.startsWith(`${scopeId}:`))
            .flatMap(([key, value]) => {
              const fieldId = key.split(":")[1];
              const formFieldId = parseInt(fieldId, 10);

              if (isNaN(formFieldId)) {
                console.warn(`Invalid form_field ID: ${fieldId}`);
                return [];
              }

              // Skip empty values
              if (
                value === "" ||
                value === null ||
                value === undefined ||
                (Array.isArray(value) && value.length === 0)
              ) {
                return [];
              }

              // If value is an array, create one response per element
              // matching the requested format:
              //   { form_field: 1, value: [v1] },
              //   { form_field: 1, value: [v2] },
              if (Array.isArray(value)) {
                return value
                  .filter((v) => v !== "" && v !== null && v !== undefined)
                  .map((v) => ({
                    form_field: formFieldId,
                    value: [String(v)],
                    verification_status: "SELF_DECLARED",
                  }));
              }

              // Single value
              return [
                {
                  form_field: formFieldId,
                  value: [String(value)],
                  verification_status: "SELF_DECLARED",
                },
              ];
            }),
        })),
      })),
  });

  // ----- Submit functions -----
  const submitEmployment = async () => {
    if (!professionalProfileId) {
      throw new Error("Professional profile ID missing.");
    }
    const payload = buildEmploymentPayload();
    const toCreate = payload.employment_records.filter(
      (rec) => !rec._backendId,
    );
    const toUpdate = payload.employment_records.filter((rec) => rec._backendId);

    if (toCreate.length > 0) {
      await axios.post(EMPLOYMENT_URL, {
        professional_id: professionalProfileId,
        employment_records: toCreate.map(({ _backendId, ...rest }) => rest),
      });
    }
    if (toUpdate.length > 0) {
      await Promise.all(
        toUpdate.map(({ _backendId, ...rest }) =>
          axios.put(`${EMPLOYMENT_URL}${_backendId}/`, rest),
        ),
      );
    }
  };

  const submitProjects = async () => {
    if (!professionalProfileId) {
      throw new Error("Professional profile ID missing.");
    }
    const payload = buildProjectPayload();
    console.log("project payload =", payload);

    const toCreate = payload.project_records.filter((rec) => !rec._backendId);
    const toUpdate = payload.project_records.filter((rec) => rec._backendId);

    if (toCreate.length > 0) {
      await axios.post(PROJECT_BULK_URL, {
        professional: professionalProfileId,
        project_records: toCreate.map(({ _backendId, ...rest }) => rest),
      });
    }

    if (toUpdate.length > 0) {
      await Promise.all(
        toUpdate.map(({ _backendId, ...rest }) =>
          axios.put(`${PROJECT_URL}${_backendId}/`, rest),
        ),
      );
    }
  };

  // ----- Delete handlers -----
  const removeEmploymentRecord = (index) => {
    const record = employmentRecords[index];
    const backendId = record ? extractEmploymentBackendId(record.id) : null;
    setEmploymentRecords((prev) => {
      if (prev.length === 1) return [createEmptyEmployment()];
      return prev.filter((_, i) => i !== index);
    });
    if (backendId) {
      axios.delete(`${EMPLOYMENT_URL}${backendId}/`).catch((err) => {
        console.error("Failed to delete employment record", err);
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: "Could not delete this record from the server.",
        });
      });
    }
  };

  const removeProjectRecord = (index) => {
    const record = projectRecords[index];
    const backendId = record ? extractProjectBackendId(record.id) : null;
    setProjectRecords((prev) => {
      if (prev.length === 1) return [createEmptyProject()];
      return prev.filter((_, i) => i !== index);
    });
    if (backendId) {
      axios.delete(`${PROJECT_URL}${backendId}/`).catch((err) => {
        console.error("Failed to delete project record", err);
        Swal.fire({
          icon: "error",
          title: "Delete Failed",
          text: "Could not delete this record from the server.",
        });
      });
    }
  };

  // ----- Validation -----
  const validate = () => {
    const errors = {};
    const hasEmployment = employmentRecords.some((rec) => rec.employer.trim());
    const hasProject = projectRecords.some(
      (rec) => rec.project_name.trim() || rec.client_name.trim(),
    );
    if (!hasEmployment && !hasProject) {
      errors.employment =
        "At least one employment or project record is required";
    }
    return errors;
  };

  // ----- Record management helpers -----
  const addRecord = (setter, creator) => {
    setter((prev) => [...prev, creator()]);
  };

  const removeRecord = (setter, index, creator) => {
    setter((prev) => {
      if (prev.length === 1) return [creator()];
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRecordChange = (setter, index, field, value) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleRecordFileChange = (
    setter,
    index,
    file,
    fileField = "evidence_file",
  ) => {
    setter((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [fileField]: file,
        file_name: file ? file.name : "",
      };
      return updated;
    });
  };

  return {
    employmentRecords,
    projectRecords,
    setEmploymentRecords,
    setProjectRecords,
    hydrateEmploymentFromRecords,
    hydrateProjectsFromRecords,
    hydrateFromRelatedRecords,
    validate,
    submitEmployment,
    submitProjects,
    removeEmploymentRecord,
    removeProjectRecord,
    addRecord,
    removeRecord,
    handleRecordChange,
    handleRecordFileChange,
  };
};
